'use client';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Badge, Button, Separator, Skeleton } from '@mokoboko/components';
import useBookedClasses from '@/state/booked-classes';
import { SubstituteBadge } from '@/components/substitute-badge';
import {
  activateOnKeyDown,
  formatClockTime,
  getAbbreviatedName,
  getFullName,
  getStartProximityLabel,
  mergeClassNames,
} from '@/lib/utils';
import { useTimeFormat } from '@/state/tenant';
import { bookingSizeText } from '@/helpers/guest-pass';
import {
  groupBookedClassesByDate,
  toBookedClassCardItem,
  toWaitlistCardItem,
  type BookedClassCardItem,
} from '@/helpers/booked-classes';
import useMyWaitlistEntries from '@/state/waitlist';
import { hasOfferEnded } from '@/helpers/waitlist';
import { EmptyCalendarState } from '@/components/empty-calendar-state';
import { ChevronRight } from 'lucide-react';

type TrailingLabel = {
  label: string;
  color: 'green' | 'orange' | 'yellow' | 'gray';
};

// A waitlist row states its place in line; everything else states how soon it
// starts. Orange renders as bold text, every other colour as a Badge.
const getTrailingLabel = (
  item: BookedClassCardItem,
  proximityLabel:
    | { label: string; color: 'green' | 'orange' }
    | null
    | undefined
): TrailingLabel | null => {
  if (!item.waitlist) return proximityLabel ?? null;
  if (item.waitlist.status === 'OFFERED') {
    return hasOfferEnded(item.waitlist)
      ? { label: 'Offer ended', color: 'gray' }
      : { label: 'Spot available', color: 'orange' };
  }
  return {
    label:
      item.waitlist.position != null
        ? `Waitlisted #${item.waitlist.position}`
        : 'Waitlisted',
    color: 'yellow',
  };
};

const TrailingLabelTag = ({
  label,
  color,
  size,
  className,
}: TrailingLabel & { size: 'sm' | 'md'; className?: string }) =>
  color === 'orange' ? (
    <span
      className={mergeClassNames(
        'text-sm font-semibold text-orange-600',
        className
      )}
    >
      {label}
    </span>
  ) : (
    <Badge label={label} color={color} size={size} className={className} />
  );

interface BookedClassCardProps {
  item: BookedClassCardItem;
  variant: 'compact' | 'full';
  proximityLabel?: { label: string; color: 'green' | 'orange' } | null;
}

const UpcomingClassesSkeleton = () => (
  <>
    {/* Mobile */}
    <div className="flex flex-col gap-6 sm:hidden">
      {Array.from({ length: 2 }).map((_, groupIndex) => (
        <div key={groupIndex} className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between">
            <Skeleton text="body-sm" width={160} />
            <Skeleton text="body-sm" width={72} />
          </div>
          <div className="flex w-full flex-col gap-4">
            {Array.from({ length: 2 }).map((__, cardIndex) => (
              <div
                key={cardIndex}
                className="flex items-stretch overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]"
              >
                <Skeleton
                  height="100%"
                  width={80}
                  className="shrink-0 self-stretch !rounded-none"
                />
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-4">
                  <Skeleton text="body" width={160} />
                  <Skeleton text="body-sm" width={128} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Desktop */}
    <div className="hidden flex-col gap-5 sm:flex">
      {Array.from({ length: 2 }).map((_, groupIndex) => (
        <div key={groupIndex} className="flex flex-row gap-4">
          <div className="flex w-[96px] shrink-0 flex-col gap-2 rounded-xl bg-gray-100 px-4 pb-4 pt-6">
            <Skeleton text="body" width={56} />
            <Skeleton text="body-sm" width={48} />
          </div>
          <div className="flex w-full flex-col gap-4">
            {Array.from({ length: 2 }).map((__, cardIndex) => (
              <div
                key={cardIndex}
                className="flex items-stretch overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]"
              >
                <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 px-5 py-4">
                  <div className="flex items-center gap-1.5">
                    <Skeleton text="body-lg" width={208} />
                    <Skeleton height={24} width={56} className="ml-auto" />
                  </div>
                  <Skeleton text="body-sm" width={160} />
                </div>
                <Skeleton
                  height="100%"
                  width={100}
                  className="shrink-0 self-stretch !rounded-none"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </>
);

const BookedClassCard = ({
  item,
  variant,
  proximityLabel,
}: BookedClassCardProps) => {
  const router = useRouter();
  const timeFormat = useTimeFormat();
  const teachingInstructor = item.substitute ?? item.instructor;
  const instructorName = teachingInstructor
    ? getFullName(teachingInstructor.first_name, teachingInstructor.last_name)
    : '';
  const displayInstructorName = teachingInstructor
    ? getAbbreviatedName(
        teachingInstructor.first_name,
        teachingInstructor.last_name
      )
    : '';
  const time = formatClockTime(item.startTime, timeFormat);
  const ariaSubject = item.waitlist
    ? `your waitlist spot for ${item.name}`
    : `${item.name} class`;
  const sizeText = bookingSizeText(item.quantity, item.guestCount ?? 0);
  const ariaLabel = `View ${ariaSubject} at ${time}${
    instructorName ? ` with ${instructorName}` : ''
  }${sizeText ? `, ${sizeText}` : ''}${
    item.guestOf ? `, as a guest of ${item.guestOf.first_name}` : ''
  }`;
  const isCompact = variant === 'compact';
  const trailingLabel = getTrailingLabel(item, proximityLabel);

  const image = item.image && (
    <div
      className={
        isCompact
          ? 'relative w-[80px] flex-shrink-0'
          : 'w-[100px] flex-shrink-0'
      }
    >
      <img src={item.image} alt="" className="h-full w-full object-cover" />
    </div>
  );

  const body = (
    <div
      className={
        isCompact
          ? mergeClassNames(
              'flex min-w-0 flex-col justify-center gap-1 px-4',
              !item.image && 'py-4'
            )
          : 'flex min-w-0 flex-1 flex-col justify-center gap-2 px-5 py-4'
      }
    >
      <p
        className={mergeClassNames(
          'flex items-center gap-1.5 font-semibold text-gray-900 group-hover:text-red-400',
          isCompact ? 'text-base' : 'text-lg'
        )}
      >
        <span>{item.name}</span>
        <ChevronRight
          className={mergeClassNames(
            'shrink-0 text-red-400 transition-transform duration-300 ease-in-out group-hover:translate-x-1',
            isCompact ? 'h-[18px] w-[18px]' : 'h-5 w-5'
          )}
        />
        {!isCompact && trailingLabel && (
          <TrailingLabelTag
            {...trailingLabel}
            size="md"
            className="ml-auto shrink-0"
          />
        )}
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        <p className="text-sm text-gray-700">{time}</p>
        {displayInstructorName && (
          <>
            <span className="text-gray-400">·</span>
            <p className="text-sm text-gray-700">{displayInstructorName}</p>
          </>
        )}
        {item.substitute && <SubstituteBadge />}
        {sizeText && (
          <>
            <span className="text-gray-400">·</span>
            <p className="text-sm text-gray-700">{sizeText}</p>
          </>
        )}
        {item.guestOf && (
          <>
            <span className="text-gray-400">·</span>
            <p className="text-sm text-gray-700">
              Guest of {item.guestOf.first_name}
            </p>
          </>
        )}
        {/* The phone row has no room beside the name, and a waitlist place
            must never read as a booking. */}
        {isCompact && trailingLabel && (
          <TrailingLabelTag {...trailingLabel} size="sm" />
        )}
      </div>
    </div>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      className="mb-focus-visible group relative flex cursor-pointer items-stretch overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] transition-all duration-200 after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:shadow-[inset_0_-2px_0_0_rgba(24,29,39,0.06)] after:ring-1 after:ring-inset after:ring-black/[0.12] hover:bg-gray-100"
      onClick={() => router.push(item.href)}
      onKeyDown={activateOnKeyDown(() => router.push(item.href))}
    >
      {isCompact ? image : body}
      {isCompact ? body : image}
    </div>
  );
};

interface UpcomingClassesProps {
  email: string;
  showCreateAccountBanner?: boolean;
  onLoginClick?: VoidFunction;
  onReady?: () => void;
}

export const UpcomingClasses = ({
  email,
  showCreateAccountBanner,
  onLoginClick,
  onReady,
}: UpcomingClassesProps) => {
  const { data: bookedClasses, isLoading: isBookedLoading } = useBookedClasses({
    email,
    type: 'upcoming',
  });
  const { data: waitlistEntries, isLoading: isWaitlistLoading } =
    useMyWaitlistEntries();
  const isLoading = isBookedLoading || isWaitlistLoading;

  const readyFired = useRef(false);
  useEffect(() => {
    if (!isLoading && !readyFired.current) {
      readyFired.current = true;
      onReady?.();
    }
  }, [isLoading, onReady]);

  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const groupedClasses = useMemo(
    () =>
      groupBookedClassesByDate([
        ...(bookedClasses ?? [])
          .map(toBookedClassCardItem)
          .filter((item): item is BookedClassCardItem => !!item),
        ...(waitlistEntries ?? []).map(toWaitlistCardItem),
      ]),
    [bookedClasses, waitlistEntries]
  );

  return (
    <>
      <Separator />
      <div className="flex flex-col gap-4 sm:gap-2.5">
        <p className="text-sm font-semibold uppercase text-gray-700">
          BOOKED CLASSES
        </p>
        {isLoading ? (
          <UpcomingClassesSkeleton />
        ) : (
          <>
            {groupedClasses.length ? (
              <>
                <div className="flex flex-col gap-6 sm:hidden">
                  {groupedClasses.map((group) => {
                    // A start countdown belongs to a booking, never to a
                    // place in line.
                    const firstBooking = group.classes.find(
                      (item) => !item.waitlist
                    );
                    const proximityLabel =
                      firstBooking &&
                      getStartProximityLabel(
                        firstBooking.startTime,
                        firstBooking.endTime
                      );
                    return (
                      <Fragment key={group.date}>
                        <div className="flex flex-col gap-2.5">
                          <div className="flex items-center justify-between text-sm text-gray-700">
                            <span>
                              {`${group.dateFormatted}, ${group.dayName}`}
                            </span>
                            {proximityLabel && (
                              <span
                                className={
                                  proximityLabel.color === 'orange'
                                    ? 'font-semibold text-orange-600'
                                    : 'font-semibold text-green-600'
                                }
                              >
                                {proximityLabel.label}
                              </span>
                            )}
                          </div>
                          <div className="flex w-full flex-col gap-4">
                            {group.classes.map((item) => (
                              <BookedClassCard
                                key={item.href}
                                item={item}
                                variant="compact"
                              />
                            ))}
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>

                <div className="hidden flex-col gap-5 sm:flex">
                  {groupedClasses.map((group) => (
                    <Fragment key={group.date}>
                      <div className="flex flex-row gap-4">
                        <div className="flex w-[96px] shrink-0 flex-col gap-2 rounded-xl bg-gray-100 px-4 pb-4 pt-6">
                          <p className="text-base font-semibold text-gray-900">
                            {group.dayName}
                          </p>
                          <p className="text-sm text-gray-700">
                            {group.dateFormatted}
                          </p>
                        </div>
                        <div className="flex w-full flex-col gap-4">
                          {group.classes.map((item) => (
                            <BookedClassCard
                              key={item.href}
                              item={item}
                              variant="full"
                              proximityLabel={getStartProximityLabel(
                                item.startTime,
                                item.endTime
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </>
            ) : (
              <EmptyCalendarState
                className="py-6"
                title="No upcoming classes"
                description="Ready to book your next class?"
              />
            )}
          </>
        )}
      </div>
      {showCreateAccountBanner && (
        <>
          <Separator />
          <div className="flex flex-col gap-4 xs:flex-row xs:items-center xs:gap-8">
            <div className="flex w-full flex-col gap-1">
              <p className="text-lg font-semibold text-gray-900">
                You already have an account
              </p>
              <p className="text-sm text-gray-700">
                Log in to book faster and manage your classes in one place.
              </p>
            </div>
            <Button variant="secondary-gray" onClick={onLoginClick} size="lg">
              Login
            </Button>
          </div>
        </>
      )}
    </>
  );
};
